import { computed, inject, Injectable } from '@angular/core';
import { OrderQuery } from './state/order.query';
import { ProductsService } from 'app/products/products.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { OrderStore } from './state/order.store';

@Injectable({
    providedIn: 'root',
})
export class OrderStepsService {
    private orderQuery = inject(OrderQuery);
    private orderStore = inject(OrderStore);
    private product = inject(ProductsService).product;

    private currentStepIndex = computed(() => this.getStepIndex(this.currentStep()));

    public steps = computed(() => this.product().orderConfig.steps);
    public currentStep = toSignal(this.orderQuery.currentStep$);
    public completedSteps = toSignal(this.orderQuery.completedSteps$);
    public invalidSteps = toSignal(this.orderQuery.invalidSteps$);

    public isFinalStep = computed(() => this.currentStepIndex() === this.steps().length - 1);
    public isLandingPage = computed(() => this.currentStep() === '');

    public getLatestStep(): string {
        const lastCompletedStep = this.completedSteps()[this.completedSteps().length - 1];
        return this.getSlug(this.getStepIndex(lastCompletedStep) + 1);
    }

    public isStepComplete(slug: string): boolean {
        return this.completedSteps().includes(slug);
    }

    public isPreviousStepComplete(slug: string): boolean {
        const stepIndex = this.getStepIndex(slug);
        const previousSlug = this.getSlug(stepIndex - 1);
        return this.isStepComplete(previousSlug);
    }

    /**
     * @description returns the name of the step that is `n` steps away.
     * @param {number} n distance from the current or given step to travel. Negative number returns
     *  a previous step.
     * @default 1
     * @param {number} from index of the step from which to traverse the steps array.
     *  If set to -1 it will take the current step's index.
     * @default -1
     * @returns {string | null} name of the step or null, if no valid step found
     */
    public traverseSteps(n: number = 1, from: number = -1): string | null {
        const steps = this.steps();
        from = from === -1 ? this.currentStepIndex() : from;
        if (from !== -1) {
            const index = Math.min(steps.length - 1, Math.max(0, from + n));
            return steps[index].slug;
        }
        return null;
    }

    /**
     * @description sets the next active step. It will not update the active step
     * when the given step is the same, it is not contained in the current product's
     * `steps` array or the current product is `undefined`. It will also not update
     * the step if neither the previous step nor the next active step is marked as
     * complete.
     *
     * There are two special values for `slug` that will result in different behavior:
     *
     * - `'first'` will always move to the first step *after* the landing page (i.e.
     * the step on index 1 of the product's `steps` list)
     * - `'landing'` will always move to the landing page for the product (i.e. the
     * step that has an empty string as slug)
     * @param {string} slug the slug of the step to set.
     * @param {boolean} markCurrentAsComplete wether to mark the currently active step
     * as complete.
     * @default false
     */
    public setStep(slug: string, markCurrentAsComplete: boolean = false): void {
        if (
            slug !== 'landing' &&
            slug !== 'first' &&
            (typeof this.product() === 'undefined' ||
                slug === this.currentStep() ||
                !this.steps().some((step) => step.slug === slug))
        ) {
            return;
        }

        const completedSteps = [...this.completedSteps()];
        if (markCurrentAsComplete && !completedSteps.includes(this.currentStep())) {
            completedSteps.push(this.currentStep());
        }

        if (slug === 'landing') {
            this.orderStore.update({ currentStep: '', completedSteps });
            return;
        }
        // 'first' is a shortcut to the first 'real' step
        // after the landing page (which is always at 0 in
        // the order of steps)
        if (slug === 'first') {
            const firstStep = this.steps().at(1).slug;
            this.orderStore.update({ currentStep: firstStep, completedSteps });
            return;
        }
        const stepBeforeTargetStep = this.traverseSteps(
            -1,
            this.steps().findIndex((item) => item.slug === slug),
        );
        if (completedSteps.includes(slug) || completedSteps.includes(stepBeforeTargetStep)) {
            this.orderStore.update({ currentStep: slug, completedSteps });
        }
    }

    private getSlug(index: number): string | undefined {
        return this.steps()[index]?.slug;
    }

    private getStepIndex(slug: string): number {
        if (!this.steps()) {
            return -1;
        }
        return this.steps().findIndex((step) => step.slug === slug);
    }
}
